/**
 * Icon catalog index + search (WL-012 tranche 2, decisions-log row 35).
 *
 * Row 35 mandate: the canonical Segment icon picker is the BIG library - "many
 * many many more icon options of all varieties" - and it must handle 1,500+
 * icons smoothly with GLOBAL search (across every category, not just the one
 * currently selected), categories, and style variants, in a virtualized grid.
 *
 * The live `SegmentSettings/children/SegmentIcon.jsx` picker only filters WITHIN
 * the selected category - that is the exact gap row 35 calls out. This pure
 * module builds ONE flat, searchable index over the whole catalog so search can
 * span all categories at once, and the `IconPicker` component renders it
 * virtualized.
 *
 * REUSE, NOT DUPLICATION (FEATURE-COMPLETENESS LAW): the icon SOURCE stays the
 * app's existing `faCategories` map (1,454 wrapped FontAwesome icons). This
 * module takes that map (or any `{ category: [icon] }` map) as INPUT and derives
 * a search index from it - it imports no icon components itself, so it is pure
 * and unit-testable with a tiny fixture.
 *
 * Pure: no React, no DOM, no I/O.
 */

/** Split a PascalCase / camelCase icon name into lowercase word tokens. */
export const tokenizeIconName = (name) => {
  if (typeof name !== "string") return [];
  // Drop a leading library prefix (Fa / Md / Bi ...) so "FaBeer" -> ["beer"].
  const stripped = name.replace(/^(Fa|Md|Bi|Bs|Ai|Fi|Gi|Hi|Io|Ri|Si|Ti|Vsc|Wi)(?=[A-Z])/, "");
  return stripped
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
};

/** A human-readable label from a raw icon name: "FaAppleAlt" -> "Apple Alt". */
export const iconLabel = (name) => {
  const toks = tokenizeIconName(name);
  if (!toks.length) return String(name || "");
  return toks.map((t) => t.charAt(0).toUpperCase() + t.slice(1)).join(" ");
};

const iconName = (icon) =>
  (icon && (icon.displayName || icon.iconName || icon.name)) || "";

/**
 * Build a flat search index from a `{ category: [iconComponent] }` map.
 *
 * Each entry: { icon, name, label, category, tokens, key }. `key` is unique
 * (name + category) so the same icon appearing in two categories is de-duped by
 * name for GLOBAL (all-category) search but kept per-category for the tabs.
 *
 * @param {Object<string, Array>} categoryMap
 * @returns {{ entries: Array, categories: string[], byCategory: Object }}
 */
export const buildIconIndex = (categoryMap) => {
  const map = categoryMap && typeof categoryMap === "object" ? categoryMap : {};
  const categories = Object.keys(map);
  const entries = [];
  const byCategory = {};
  const globalSeen = new Set();

  categories.forEach((category) => {
    const list = Array.isArray(map[category]) ? map[category] : [];
    byCategory[category] = [];
    list.forEach((icon) => {
      const name = iconName(icon);
      if (!name) return;
      const entry = {
        icon,
        name,
        label: iconLabel(name),
        category,
        tokens: tokenizeIconName(name),
        key: `${category}:${name}`,
      };
      entries.push(entry);
      byCategory[category].push(entry);
      globalSeen.add(name);
    });
  });

  return { entries, categories, byCategory, total: globalSeen.size };
};

/**
 * Filter an index for the picker.
 *
 * GLOBAL search (row 35): when `query` is non-empty the search spans EVERY
 * category (ignoring the category filter) so a user never has to guess which
 * tab an icon lives in. With no query, the `category` filter selects one tab
 * ("All" or undefined => everything). De-duplicates by icon name in global
 * search so the same icon in two categories shows once.
 *
 * Ranking: exact-token match > token prefix > substring, then alphabetical -
 * so typing "car" surfaces "Car" before "Carrot"/"Scarf".
 *
 * @param {ReturnType<typeof buildIconIndex>} index
 * @param {{ query?: string, category?: string }} [opts]
 * @returns {Array} matching entries (ordered)
 */
export const searchIcons = (index, opts = {}) => {
  const idx = index || { entries: [], byCategory: {} };
  const q = String(opts.query || "").trim().toLowerCase();
  const category = opts.category;

  if (!q) {
    if (!category || category === "All") return idx.entries;
    return idx.byCategory[category] || [];
  }

  // Global search across all categories, de-duped by name.
  const seen = new Set();
  const scored = [];
  idx.entries.forEach((e) => {
    if (seen.has(e.name)) return;
    let score = -1;
    const tokens = e.tokens;
    if (tokens.includes(q)) score = 0; // exact token
    else if (tokens.some((t) => t.startsWith(q))) score = 1; // token prefix
    else if (e.name.toLowerCase().includes(q) || e.label.toLowerCase().includes(q)) score = 2;
    if (score >= 0) {
      seen.add(e.name);
      scored.push({ e, score });
    }
  });
  scored.sort((a, b) => a.score - b.score || a.e.name.localeCompare(b.e.name));
  return scored.map((s) => s.e);
};

/** Category tab list for the picker: "All" first, then the source categories. */
export const iconCategories = (index) => ["All", ...((index && index.categories) || [])];
